/**
 * One finding on its own page: a regulatory finding or a gap suggestion
 * (control amendment, missing document) — the queue lists both, and both
 * open here. The decision is the organisation's: resolve against a version
 * (or a note on the current one), dismiss with a reason, or reopen.
 *
 * @module routes/$org.compliance.findings.$findingId
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeftIcon, ScanSearchIcon } from "lucide-react";

import { ComplianceOrganizationMissing, ComplianceUnavailable } from "../components/regulation/ComplianceUnavailable";
import { ListSkeleton } from "../components/regulation/LoadingSkeletons";
import { FindingDetailPage } from "../components/regulation/finding/FindingDetailPage";
import { useFindingItems } from "../components/regulation/finding/useFindingItems";
import { validateReviewSearch } from "../components/regulation/reviewPageSearch";
import { useRegulationOrg } from "../components/regulation/useRegulationOrg";
import { useOrgProfile } from "../state/orgProfile";

function Frame({ orgSegment, children }: { readonly orgSegment: string; readonly children: React.ReactNode }) {
  return (
    <div data-regulation-page="true" className="relative flex min-h-0 flex-1 flex-col">
      <header className="flex h-11 shrink-0 items-center gap-2 border-b border-border/60 px-4 text-[13px]">
        <Link to="/$org/compliance/findings" params={{ org: orgSegment }} className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground">
          <ArrowLeftIcon aria-hidden className="size-4" />
          Findings
        </Link>
      </header>
      <div className="mx-auto w-full max-w-[60rem] px-4 py-8 sm:px-8">{children}</div>
    </div>
  );
}

function FindingRoute() {
  const { org: orgSegment, findingId } = Route.useParams();
  const search = Route.useSearch();
  const { org, isLoading } = useRegulationOrg();
  const organizationId = org?.id ?? null;
  const view = useFindingItems(organizationId);
  const { canManage } = useOrgProfile(organizationId);
  const item = view.items.find((candidate) => candidate.id === findingId) ?? null;

  if (organizationId === null && !isLoading) return <Frame orgSegment={orgSegment}><ComplianceOrganizationMissing /></Frame>;
  if (view.unavailableReason) return <Frame orgSegment={orgSegment}><ComplianceUnavailable reason={view.unavailableReason} onRetry={view.refresh} /></Frame>;
  if (organizationId === null || !view.ready) return <Frame orgSegment={orgSegment}><ListSkeleton rows={4} /></Frame>;
  if (item === null) {
    return (
      <Frame orgSegment={orgSegment}>
        <div className="flex flex-col items-center gap-2 py-16 text-center text-[13px] text-muted-foreground">
          <ScanSearchIcon aria-hidden className="size-6 text-muted-foreground/50" />
          <p className="font-medium text-foreground">Finding not found</p>
          <p>It is not in {org?.name ?? "your organization"}'s record, or it was withdrawn.</p>
        </div>
      </Frame>
    );
  }
  return (
    <FindingDetailPage
      key={item.id}
      organizationId={organizationId}
      orgSegment={orgSegment}
      item={item}
      items={view.items}
      people={view.people}
      peopleById={view.peopleById}
      personName={view.personName}
      canManage={canManage}
      actions={view.actions}
      fromPolicy={search.fromPolicy}
      initialInspector={search.compose === "inspector"}
    />
  );
}

export const Route = createFileRoute("/$org/compliance/findings/$findingId")({
  validateSearch: validateReviewSearch,
  component: FindingRoute,
});
